<template>
  <StepperTemplate :returnRoute="{ to: homeRoute }" :narrowBody="true" :showSteps="false">
    <template v-slot:title>
      <span class="text-center text-blue-500 d-none-print" v-orphans>
        <p class="h2-stepper-title font-weight-bold mb-3">
          {{ $t('form.summary.traditional.title') }}
        </p>
        <p>{{ $t('form.summary.traditional.subtitle') }}</p>
        <p>{{ $t('form.summary.traditional.makePayment') }}</p>
        <p v-html="$t('form.summary.traditional.contactUs')"></p>
      </span>
    </template>
    <template v-slot:body>
      <BankTransferData
        :amount="getSelectedOrOtherAmount"
        :order="orderName"
        class="text-blue-500 stepper-body-card"
      />
    </template>
    <template v-slot:bottomSection>
      <div class="text-center mb-3">
        <ShareNetwork
          network="facebook"
          title=""
          :url="urlToCopy"
          class="text-decoration-none d-none-print"
        >
          <b-button
            variant="outline-primary"
            size="lg"
            class="text-base font-weight-bold ml-auto summary-social-button"
          >
            <span class="summary-social-traditional-facebook-icon mr-3" />
            {{ t('form.summary.traditional.shareButtonText', translationId) }}
          </b-button>
        </ShareNetwork>
      </div>
    </template>
  </StepperTemplate>
</template>

<script lang="ts" setup>
import StepperTemplate from '@/components/widgets/StepperTemplate.vue'
import BankTransferData from '@/components/widgets/bankTransferData.vue'
import { SOFT_PAYMENT_LIMIT } from '@/constants/payment'
import { storeToRefs } from 'pinia'
import useTransactionDataStore from '@/stores/transactionData'
import { PaymentType } from '@/types'
import type { SummaryData, Maybe } from '@/types'
import { useSummaryDataStore } from '@/stores/summary'
import useDataLayerStore from '@/stores/dataLayer'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
// @ts-ignore
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

const store = useStore()
const router = useRouter()
const { t } = useI18n()

const dataLayerStore = useDataLayerStore()
dataLayerStore.sendInfoToGtm()

const transactionDataStore = useTransactionDataStore()
const { paymentType, getSelectedOrOtherAmount, transactionData } = storeToRefs(transactionDataStore)

const summaryDataStore = useSummaryDataStore()

const storageData = ref<Maybe<SummaryData>>()
storageData.value = summaryDataStore.getSummaryData
summaryDataStore.reset()

const homeRoute = {
  name: 'Home',
  text: t('form.summary.backButton')
}

// @TODO: rewrite vuex modules to pinia and add proper types
const paymentId = computed(() => store.getters['payment/getPaymentId'])

const childId = computed(() => store.getters['child/getChild'].id)

const childEditionRetentionHash = computed<Maybe<string>>(
  () => storageData.value?.childEditionRetentionHash
)

const getPaidAmount = computed(() => store.getters['child/getPaidAmount'])

const donorName = computed<string>(
  () => getCompanyName.value || `${getDonor.value?.firstName} ${getDonor.value?.lastName}`
)

const orderName = computed<string>(() => `${donorName.value} - ${paymentId.value}`)

const translationId = computed<0 | 1>(() => {
  const FUNDRAISING_CLOSED = 1
  const FUNDRAISING_OPEN = 0

  const isCoop: boolean = paymentType.value === PaymentType.fundraising

  const paymentLimit: number = SOFT_PAYMENT_LIMIT * 100

  const isFundraisingDonateClosed: boolean =
    paymentType.value === PaymentType.fundraisingDonate &&
    getPaidAmount.value + (getSelectedOrOtherAmount.value ?? 0) * 100 < paymentLimit

  if (isCoop || isFundraisingDonateClosed) {
    return FUNDRAISING_OPEN
  }
  return FUNDRAISING_CLOSED
})

const urlBase: string = window.location.origin

const urlToChildCard = computed<string>(() => {
  const routeObject = childEditionRetentionHash.value
    ? {
        name: 'ChildPageRetention',
        params: {
          childEditionId: childId.value,
          childEditionRetentionHash: childEditionRetentionHash.value
        }
      }
    : {
        name: 'ChildPage',
        params: {
          childEditionId: childId.value
        }
      }
  return router.resolve(routeObject).href
})

const urlToHome: string = router.resolve({ name: 'Home' }).href

const urlToCopy = computed<string>(
  () => urlBase + (translationId.value === 0 ? urlToHome : urlToChildCard.value)
)

const getDonor = computed(() => transactionData.value?.donor || null)

const getCompanyName = computed(() => transactionData.value?.companyName || null)

store.commit('child/clearChildEditionRetentionHash')
</script>
